import { GanttColumn, GanttRememberColumnState, GanttRememberState } from './gantt.types';

const GANTT_REMEMBER_VERSION = 1;

export function createGanttRememberState(state: Partial<GanttRememberState> = {}): GanttRememberState {
    return {
        version: GANTT_REMEMBER_VERSION,
        ...state,
        updatedAt: new Date().toISOString(),
    };
}

export function isGanttRememberState(value: unknown): value is GanttRememberState {
    return !!value && !Array.isArray(value) && typeof value === 'object' && (value as GanttRememberState).version === GANTT_REMEMBER_VERSION;
}

export function applyRememberedColumnState(columns: GanttColumn[], state: GanttRememberColumnState | undefined): GanttColumn[] {
    if (!state) return columns;

    const widths = state.widths ?? {};
    // Only explicit user choices override visibility; old hidden defaults are ignored.
    const orderedPropertyKeys = state.orderedPropertyKeys ?? [];
    const columnsByKey = new Map(columns.map((column) => [getColumnPropertyKey(column), column]));

    const orderedColumns = orderedPropertyKeys
        .map((propertyKey) => columnsByKey.get(propertyKey))
        .filter((column): column is GanttColumn => !!column);

    const rememberedKeys = new Set(orderedColumns.map(getColumnPropertyKey));
    const newColumns = columns.filter((column) => !rememberedKeys.has(getColumnPropertyKey(column)));

    return [...orderedColumns, ...newColumns].map((column) => {
        const propertyKey = getColumnPropertyKey(column);
        const rememberedWidth = widths[propertyKey];

        return {
            ...column,
            width: rememberedWidth ?? column.width,
            ...(typeof state.visibility?.[propertyKey] === 'boolean' ? { hidden: !state.visibility[propertyKey], hide: !state.visibility[propertyKey] } : {}),
        };
    });
}

export function createRememberedColumnState(columns: GanttColumn[], visibility?: Record<string, boolean>): GanttRememberColumnState {
    const widths: Record<string, number | string> = {};
    const orderedPropertyKeys = columns.map((column) => {
        const propertyKey = getColumnPropertyKey(column);

        if (column.width !== undefined) {
            widths[propertyKey] = column.width;
        }

        return propertyKey;
    });

    return {
        orderedPropertyKeys,
        widths,
        ...(visibility && Object.keys(visibility).length ? { visibility: { ...visibility } } : {}),
    };
}

export function getColumnPropertyKey(column: Pick<GanttColumn, 'propertyKey' | 'name'>): string {
    return column.propertyKey ?? column.name ?? '';
}
